/**
 * Battery and lift measurement rules (ICD v0.2 section 9, ADR 0012, ADR 0017).
 *
 * The device is authoritative (ICD v0.2 section 9): it computes SoC, detects lift cycles, estimates the
 * load with the calibration it received by config, and detects charge sessions, deep discharge and
 * overtemperature. The cloud only stores and shows what the device reports; its own load estimate
 * (fts_lift_load_kg in the migration) is a fallback for lift events without load_kg, flagged "estimated".
 * Pure helpers shared by the simulator (reference device behaviour), the database, the console and tests.
 *
 * ADR 0003 / ADR 0012: the modules only measure and report. Nothing here, and no alarm built on it,
 * can act on the relay, driving or lifting.
 */
import type { BatteryType, ConfigCommand } from "./icd/schemas.js";

/**
 * Reference device behaviour (simulator). The deep discharge and overtemperature thresholds are
 * device-internal: ICD v0.2 section 9 has no config field for them.
 */
export const BATTERY_DEFAULTS = {
  /** deep_discharge when the SoC falls below this (%), device-internal. */
  deepSoc: 20,
  /** bat_overtemp at or above this (degC), device-internal. */
  overtempC: 55,
  /** Re-arm after the SoC rose this many points above deepSoc. */
  socHysteresis: 5,
  /** Re-arm after the temperature fell this many degrees below overtempC. */
  tempHysteresis: 5,
  /** |current| below this (A) counts as neither charging nor discharging (sensor offset). */
  currentDeadbandA: 2,
  /** A battery reading older than this no longer tells the charging state (ms). */
  readingFreshMs: 10 * 60_000,
  /** A charge_start without charge_end older than this is not "charging now" (ms). */
  maxOpenChargeMs: 24 * 3600_000,
  /** Reference device: a current direction held this long starts / ends a charge session (s). */
  chargeDetectS: 30,
} as const;

export const LIFT_DEFAULTS = {
  /** Reference device: lift_bar_threshold until configured (bar). */
  thresholdBar: 40,
} as const;

/* ---------------- module settings pushed to the device (config, ICD v0.2 section 9) ---------------- */

export type ModuleSettings = {
  batType: BatteryType | null;
  batAh: number | null;
  liftKgPerBar: number | null;
  liftBarThreshold: number | null;
};

/** Same bounds as configCommand and the vehicles columns. */
export const MODULE_SETTING_LIMITS = {
  batAh: { min: 10, max: 5000 },
  liftKgPerBar: { min: 0.1, max: 1000 },
  liftBarThreshold: { min: 1, max: 250 },
} as const;

/** The flat config fields for the settings that are set (null = not sent, the device keeps its value). */
export function moduleConfigFields(s: ModuleSettings): Partial<Pick<ConfigCommand, "bat_type" | "bat_ah" | "lift_kg_per_bar" | "lift_bar_threshold">> {
  return {
    ...(s.batType !== null ? { bat_type: s.batType } : {}),
    ...(s.batAh !== null ? { bat_ah: s.batAh } : {}),
    ...(s.liftKgPerBar !== null ? { lift_kg_per_bar: s.liftKgPerBar } : {}),
    ...(s.liftBarThreshold !== null ? { lift_bar_threshold: s.liftBarThreshold } : {}),
  };
}

/* ---------------- lift load estimate ---------------- */

/**
 * Per-vehicle calibration. kgPerBar (vehicles.lift_kg_per_bar) is pushed to the device as lift_kg_per_bar.
 * emptyBar (vehicles.lift_empty_bar) is cloud-only: the tare of the FALLBACK estimate (ICD v0.2 section 9
 * does not say how the device handles the empty-fork pressure); null = 0.
 */
export type LiftCalibration = {
  emptyBar: number | null;
  kgPerBar: number | null;
  ratedCapacityKg?: number | null;
};

export const LIFT_CALIBRATION_LIMITS = {
  emptyBar: { min: 0, max: 100 },
  kgPerBar: { min: 0.1, max: 1000 },
  ratedCapacityKg: { min: 100, max: 50_000 },
} as const;

const finite = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

export function isCalibrated(c: LiftCalibration | null | undefined): boolean {
  return !!c && finite(c.kgPerBar) && c.kgPerBar > 0 && (c.emptyBar === null || (finite(c.emptyBar) && c.emptyBar >= 0));
}

/**
 * Cloud FALLBACK load estimate (kg) from the peak pressure: (peak - emptyBar) x kg/bar, never negative,
 * rounded to 10 kg; null without a calibration. Same rule as public.fts_lift_load_kg.
 */
export function estimateLoadKg(peakBar: number | null | undefined, cal: LiftCalibration | null | undefined): number | null {
  if (!isCalibrated(cal) || !finite(peakBar) || peakBar < 0) return null;
  const kg = Math.max(0, (peakBar - (cal!.emptyBar ?? 0)) * cal!.kgPerBar!);
  return Math.round(kg / 10) * 10;
}

/** The device's load_kg wins; the cloud estimate is only used without it and is flagged. */
export function resolveLoadKg(
  deviceLoadKg: number | null | undefined,
  peakBar: number | null | undefined,
  cal: LiftCalibration | null | undefined,
): { loadKg: number | null; estimated: boolean } {
  if (finite(deviceLoadKg) && deviceLoadKg >= 0) return { loadKg: deviceLoadKg, estimated: false };
  const kg = estimateLoadKg(peakBar, cal);
  return { loadKg: kg, estimated: kg !== null };
}

/** Share of the rated capacity (0..), null when either side is unknown. */
export function capacityShare(loadKg: number | null, ratedCapacityKg: number | null | undefined): number | null {
  return finite(loadKg) && finite(ratedCapacityKg) && ratedCapacityKg > 0 ? loadKg / ratedCapacityKg : null;
}

/** Pressure (bar) a load produces on a truck with this tare and factor (simulator physics). */
export function pressureForLoad(loadKg: number, cal: { emptyBar: number; kgPerBar: number }): number {
  return cal.emptyBar + Math.max(0, loadKg) / cal.kgPerBar;
}

/* ---------------- threshold events with hysteresis (device behaviour) ---------------- */

export type ThresholdState = { armed: boolean };

/**
 * Edge detector for deep_discharge (direction "below") and bat_overtemp ("above"): fires once when the
 * value crosses the threshold, re-arms only after it recovered by `hysteresis`. A missing value changes nothing.
 */
export function stepThreshold(
  state: ThresholdState,
  value: number | null | undefined,
  threshold: number,
  hysteresis: number,
  direction: "below" | "above",
): { state: ThresholdState; fire: boolean } {
  if (!finite(value)) return { state, fire: false };
  const crossed = direction === "below" ? value < threshold : value >= threshold;
  const recovered = direction === "below" ? value >= threshold + hysteresis : value <= threshold - hysteresis;
  if (state.armed && crossed) return { state: { armed: false }, fire: true };
  if (!state.armed && recovered) return { state: { armed: true }, fire: false };
  return { state, fire: false };
}

export const deepDischargeStep = (s: ThresholdState, soc: number | null | undefined, deepSoc: number = BATTERY_DEFAULTS.deepSoc) =>
  stepThreshold(s, soc, deepSoc, BATTERY_DEFAULTS.socHysteresis, "below");

export const overtempStep = (s: ThresholdState, tempC: number | null | undefined, overtempC: number = BATTERY_DEFAULTS.overtempC) =>
  stepThreshold(s, tempC, overtempC, BATTERY_DEFAULTS.tempHysteresis, "above");

/* ---------------- charge sessions ---------------- */

export type ChargeEventRecord = {
  type: "charge_start" | "charge_end";
  /** Effective event time (ISO). */
  at: string;
  soc?: number | null;
  ah?: number | null;
  /** charge_end (ICD v0.2 section 9). */
  kwh?: number | null;
};

export type ChargeSession = {
  /** null: the end arrived without its start (start lost or before the window). */
  startAt: string | null;
  /** null: still charging, or the end never arrived (see `open`). */
  endAt: string | null;
  startSoc: number | null;
  endSoc: number | null;
  ah: number | null;
  kwh: number | null;
  /** Charging now: the newest start, no end yet, and not older than maxOpenChargeMs. */
  open: boolean;
  /** Start or end is missing (power loss, lost message); the session is shown but not trusted for totals. */
  incomplete: boolean;
};

const t = (iso: string): number => {
  const v = Date.parse(iso);
  return Number.isFinite(v) ? v : Number.NaN;
};

/**
 * Pair charge_start / charge_end events into sessions, newest last. Events are ordered by time first
 * (buffered data arrives late). A start followed by another start leaves the first one incomplete; an end
 * without a start becomes an incomplete session without a start.
 * Duplicates (same type and time) are ignored.
 */
export function pairChargeSessions(events: readonly ChargeEventRecord[], now: Date = new Date()): ChargeSession[] {
  const seen = new Set<string>();
  const sorted = events
    .filter((e) => (e.type === "charge_start" || e.type === "charge_end") && Number.isFinite(t(e.at)))
    .filter((e) => {
      const k = `${e.type}@${t(e.at)}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .sort((a, b) => t(a.at) - t(b.at) || (a.type === "charge_start" ? -1 : 1));
  const out: ChargeSession[] = [];
  let cur: ChargeSession | null = null;
  const num = (v: number | null | undefined) => (finite(v) ? v : null);
  for (const e of sorted) {
    if (e.type === "charge_start") {
      if (cur) out.push({ ...cur, incomplete: true });
      cur = { startAt: e.at, endAt: null, startSoc: num(e.soc), endSoc: null, ah: null, kwh: null, open: false, incomplete: false };
    } else if (cur) {
      out.push({ ...cur, endAt: e.at, endSoc: num(e.soc), ah: num(e.ah), kwh: num(e.kwh) });
      cur = null;
    } else {
      out.push({ startAt: null, endAt: e.at, startSoc: null, endSoc: num(e.soc), ah: num(e.ah), kwh: num(e.kwh), open: false, incomplete: true });
    }
  }
  if (cur) {
    const fresh = now.getTime() - t(cur.startAt!) <= BATTERY_DEFAULTS.maxOpenChargeMs;
    out.push({ ...cur, open: fresh, incomplete: !fresh });
  }
  return out;
}

export const CHARGE_STATES = ["charging", "discharging", "resting", "unknown"] as const;
export type ChargeState = (typeof CHARGE_STATES)[number];

/**
 * Charging state now. A fresh current reading wins (sign: + charge, - discharge, ICD v0.2 section 9);
 * without one, an open charge session means charging; otherwise unknown.
 */
export function chargeState(
  latest: { batA: number | null | undefined; batAt: string | null | undefined } | null,
  sessions: readonly ChargeSession[],
  now: Date = new Date(),
): ChargeState {
  const at = latest?.batAt ? t(latest.batAt) : Number.NaN;
  const fresh = Number.isFinite(at) && now.getTime() - at <= BATTERY_DEFAULTS.readingFreshMs;
  if (fresh && finite(latest?.batA)) {
    const a = latest!.batA!;
    if (a >= BATTERY_DEFAULTS.currentDeadbandA) return "charging";
    if (a <= -BATTERY_DEFAULTS.currentDeadbandA) return "discharging";
    return sessions.at(-1)?.open ? "charging" : "resting";
  }
  return sessions.at(-1)?.open ? "charging" : "unknown";
}

/** Newest completed (or incomplete but ended) session: "last charge" in the console. */
export function lastFinishedCharge(sessions: readonly ChargeSession[]): ChargeSession | null {
  for (let i = sessions.length - 1; i >= 0; i--) if (sessions[i]!.endAt !== null) return sessions[i]!;
  return null;
}

/**
 * UI-only "low" level (%) for an amber colour. NOT a device threshold: the cloud does not know the device's
 * deep discharge threshold (ICD v0.2 section 9 has no config field for it).
 */
export const UI_LOW_SOC = 30;

/** How long a deep_discharge event keeps the battery "low" (red) unless a charge starts. */
export const DEEP_DISCHARGE_WINDOW_MS = 24 * 3600_000;

/**
 * The device reported deep_discharge within the last 24 h and no charge_start came after it. This, not an
 * assumed percentage, makes the console show the battery as critically low.
 */
export function deepDischargeActive(lastDeepDischargeAt: string | null | undefined, chargeEvents: readonly ChargeEventRecord[], now: Date = new Date()): boolean {
  const at = lastDeepDischargeAt ? t(lastDeepDischargeAt) : Number.NaN;
  if (!Number.isFinite(at) || now.getTime() - at > DEEP_DISCHARGE_WINDOW_MS || at > now.getTime() + 60_000) return false;
  return !chargeEvents.some((e) => e.type === "charge_start" && t(e.at) > at);
}

/** Colour level: "low" (red) only on an active deep discharge from the device; "warn" (amber) below UI_LOW_SOC. */
export function socLevel(soc: number | null | undefined, deepDischarge = false): "low" | "warn" | "ok" | null {
  if (!finite(soc)) return deepDischarge ? "low" : null;
  return deepDischarge ? "low" : soc < UI_LOW_SOC ? "warn" : "ok";
}
